import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

const APIKEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/**
 * Marker and route ink, as a literal `0xRRGGBB`: a Google URL parameter cannot
 * resolve a CSS variable, which is why the old Tailwind-config read of
 * `theme.colors.mainBlack` shipped a literal `color:var(--mainBlack)` and never
 * drew black.
 *
 * TODO: derive this from the package instead of pinning it. The hexes are on
 * the theme at `theme.outpave.tokens[scheme]["color-text-primary"]`, so a
 * `useTheme()` read plus `.replace("#", "0x")` gives a token-driven value that
 * follows the brand and the scheme. Left as a constant here because that is a
 * behaviour change (the map ink would start following the theme) and belongs
 * with the wider map work, not the token sweep.
 */
export const MAP_INK = "0x000000";

interface StaticMapsImageProps {
  tripFrom: { geocode?: { x: number; y: number } };
  tripTo: { geocode?: { x: number; y: number } };
  polyline?: string;
  width?: string;
  height?: string;
  borderRadius?: string;
  mapType?: string;
}

const StaticMapsImage: React.FC<StaticMapsImageProps> = ({
  tripFrom,
  tripTo,
  polyline,
  width = "100%",
  height = "auto",
  borderRadius = "8px",
  mapType = "roadmap",
}) => {
  const { t } = useTranslation();
  const [imageError, setImageError] = useState(false);
  const [triedWithoutPolyline, setTriedWithoutPolyline] = useState(false);

  const generateGoogleMapStaticImageUrl = (ignorePolyline = false) => {
    if (!tripFrom?.geocode || !tripTo?.geocode) return null;

    const start = tripFrom.geocode;
    const end = tripTo.geocode;

    // Only use polyline if not ignoring and polyline exists
    const pathCode =
      !ignorePolyline && polyline
        ? `&path=color:${MAP_INK}|weight:6|enc:${polyline}`
        : "";

    return `https://maps.googleapis.com/maps/api/staticmap?&scale=2&size=600x300&maptype=${mapType}&markers=color:${MAP_INK}|label:A|${start.y},${start.x}&markers=color:${MAP_INK}|label:B|${end.y},${end.x}${pathCode}&key=${APIKEY}`;
  };

  const staticMapUrl = generateGoogleMapStaticImageUrl(triedWithoutPolyline);

  if (!staticMapUrl || (imageError && triedWithoutPolyline)) {
    return (
      <Box
        sx={{
          width,
          height,
          borderRadius,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "var(--color-bg-elevated)",
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {t("Map unavailable for this trip")}
        </Typography>
      </Box>
    );
  }

  return (
    <img
      src={staticMapUrl}
      alt="Static Map"
      style={{
        width,
        height,
        borderRadius,
      }}
      onError={() => {
        if (!triedWithoutPolyline && polyline) {
          setTriedWithoutPolyline(true);
          setImageError(false);
        } else {
          setImageError(true);
        }
      }}
    />
  );
};

export default StaticMapsImage;
